import { Outlet, Link } from 'react-router-dom';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AppMenu } from 'components/AppMenu';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { CompanySelect } from '../pages/empresas/Company.Select';
import { classNames } from 'primereact/utils';
import { Image } from 'components/ImageViewer';
import useListPage from 'hooks/useListPage';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Menu } from 'primereact/menu';
import PrimeReact from 'primereact/api';
import useApp from 'hooks/useApp';
import useAuth from 'hooks/useAuth';
import useLocalStore from 'hooks/useLocalStore';
import useUtils from 'hooks/useUtils';

const MainLayout = (props) => {
	const auth = useAuth();
	const app = useApp();
	const utils = useUtils();
	const localStore = useLocalStore();

	const { setempid, empid } = app;
	const appName = process.env.REACT_APP_NAME || "ELETRO";
	const userPages = auth.userPages;

	const [staticMenuInactive, setStaticMenuInactive] = useState(false);
	const [overlayMenuActive, setOverlayMenuActive] = useState(false);
	const [mobileMenuActive, setMobileMenuActive] = useState(false);
	const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);

	const filterSchema = useMemo(() => ({
		search: { tagTitle: "Search", value: '', valueType: 'single', options: [] }
	}), []);

	const pageController = useListPage(props, filterSchema);
	const { setReset, records } = pageController;

	const menuClickRef = useRef(false);
	const mobileTopbarMenuClickRef = useRef(false);
	const userMenuRef = useRef(null);

	PrimeReact.ripple = true;
	const layoutMode = 'static';
	const EMP_DEFAULT = 2;

	const isDesktop = useCallback(() => {
		return window.innerWidth >= 992;
	}, []);

	// Abertura do diálogo de seleção de empresa com fallback para desenvolvimento
	const handleCompanySelect = useCallback((forceDialog = true) => {
		if (!forceDialog && process.env.NODE_ENV === 'development' && empid == null) {
			setempid(EMP_DEFAULT);
			return;
		}

		app.openPageDialog(
			<CompanySelect />,
			{ closeBtn: true, seamless: false, width: "460px" }
		);
	}, [app, empid, setempid]);

	// Inicialização e persistência do ID da empresa
	useEffect(() => {
		if (empid == null) {
			handleCompanySelect(false);
		} else {
			localStore.setTenancy(empid);
			setReset(empid);
		}
	}, [empid, handleCompanySelect, localStore, setReset]);

	// Nome da empresa formatado de forma declarativa e reativa (DDD/Clean Code)
	const empresaNome = useMemo(() => {
		if (empid == null) return "Nenhuma empresa selecionada";
		if (records && Array.isArray(records)) {
			const found = records.find((item) => String(item.value) === String(empid));
			if (found?.label) return found.label;
		}
		return `Empresa ${empid}`;
	}, [empid, records]);

	const onWrapperClick = () => {
		if (!menuClickRef.current) {
			setOverlayMenuActive(false);
			setMobileMenuActive(false);
		}

		if (!mobileTopbarMenuClickRef.current) {
			setMobileTopbarMenuActive(false);
		}

		mobileTopbarMenuClickRef.current = false;
		menuClickRef.current = false;
	};

	const onToggleMenuClick = (event) => {
		menuClickRef.current = true;
		if (isDesktop()) {
			if (layoutMode === 'overlay') {
				setOverlayMenuActive((prev) => !prev);
				setMobileMenuActive(false);
			} else if (layoutMode === 'static') {
				setStaticMenuInactive((prev) => !prev);
			}
		} else {
			setMobileMenuActive((prev) => !prev);
		}
		event.preventDefault();
	};

	const onSidebarClick = () => {
		menuClickRef.current = true;
	};

	const onMenuItemClick = (event) => {
		if (!event.item.items) {
			setOverlayMenuActive(false);
			setMobileMenuActive(false);
		}
	};

	// Mapeamento e filtragem de menus de navegação
	const userPagesArray = useMemo(() => (userPages || []).map((x) => x.router), [userPages]);

	const navbarSideLeft = useMemo(() => {
		return utils.filterMenuChield(app.menus.navbarSideLeft, userPagesArray);
	}, [app.menus.navbarSideLeft, userPagesArray, utils]);

	const navbarTopLeft = useMemo(() => {
		return (app.menus.navbarTopLeft || []).filter((menu) => auth.canView(menu.to));
	}, [app.menus.navbarTopLeft, auth]);

	const navbarTopRight = useMemo(() => {
		return (app.menus.navbarTopRight || []).filter((menu) => auth.canView(menu.to));
	}, [app.menus.navbarTopRight, auth]);

	const containerClass = useMemo(() => {
		return {
			'layout-overlay': layoutMode === 'overlay',
			'layout-static': layoutMode === 'static',
			'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
			'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
			'layout-mobile-sidebar-active': mobileMenuActive,
			'p-input-filled': false
		};
	}, [layoutMode, mobileMenuActive, overlayMenuActive, staticMenuInactive]);

	const wrapperClass = classNames('layout-wrapper', containerClass);

	const appBarActionMenu = useMemo(() => [
		{
			label: "Minha Conta",
			icon: "pi pi-user",
			command: () => app.navigate('/account')
		},
		{
			separator: true
		},
		{
			label: "Sair",
			icon: "pi pi-power-off",
			className: "text-red-500 font-bold",
			command: () => auth.logout()
		}
	], [app, auth]);

	if (auth.loading) {
		return (
			<div style={{ height: '100vh' }} className="flex flex-column align-items-center justify-content-center gap-3">
				<ProgressSpinner style={{ width: '50px', height: '50px' }} />
				<span className="text-gray-500 font-medium">Carregando ambiente...</span>
			</div>
		);
	}

	if (!auth.user) {
		return null;
	}

	return (
		<div className={wrapperClass} onClick={onWrapperClick}>
			<style>{`
				.user-topbar-pill {
					background: #ffffff !important;
					border: 1px solid rgba(0, 0, 0, 0.12) !important;
					color: #1e293b !important;
					outline: none;
				}
				.user-topbar-pill:hover, .user-topbar-pill:focus {
					background: #f8fafc !important;
					box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
					transform: translateY(-1px);
				}
				.user-avatar-circle {
					background: var(--primary-color, #2563eb) !important;
					color: #ffffff !important;
					width: 32px !important;
					height: 32px !important;
					font-size: 14px !important;
					box-shadow: 0 2px 5px rgba(0,0,0,0.15);
				}
				.user-name-label {
					color: #1e293b !important;
					max-width: 140px;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				.user-chevron-icon {
					color: #475569 !important;
					font-weight: bold;
				}
			`}</style>

			{/* Topbar moderna e fluida */}
			<div className="layout-topbar bg-primary shadow-4 flex align-items-center justify-content-between px-3">
				<div className="flex align-items-center gap-2">
					<div className="layout-toggle-menu">
						<Button
							type="button"
							icon="pi pi-bars"
							className="p-button-text p-button-rounded text-white"
							onClick={onToggleMenuClick}
							tooltip="Alternar menu lateral"
							tooltipOptions={{ position: "bottom" }}
						/>
					</div>

					<Link to="/home" className="layout-topbar-logo flex align-items-center gap-2 text-white no-underline">
						<img
							src="/images/logo.png"
							alt="logo"
							style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
						/>
						<span className="text-white font-bold text-lg hidden sm:inline">{appName}</span>
					</Link>
				</div>

				{/* Badge de Empresa Selecionada com Troca Rápida */}
				<div className="flex align-items-center mx-2">
					<div
						className={classNames(
							"company-selector-pill flex align-items-center gap-2 px-3 py-1 border-round-3xl transition-all transition-duration-200",
							{
								"cursor-pointer hover:shadow-2 surface-hover": auth.isadmin,
								"surface-card": true
							}
						)}
						style={{
							border: "1px solid rgba(255, 255, 255, 0.15)",
							background: "rgba(255, 255, 255, 0.12)",
							backdropFilter: "blur(6px)"
						}}
						onClick={() => auth.isadmin && handleCompanySelect(true)}
						title={auth.isadmin ? "Clique para trocar de empresa" : "Empresa atual"}
					>
						<i className="pi pi-building text-white text-base" />
						<span
							className="font-semibold text-sm text-white"
							style={{
								maxWidth: '240px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap'
							}}
						>
							{empresaNome}
						</span>
						{auth.isadmin && (
							<span
								className="text-xs bg-white text-primary px-2 py-0 border-round-xl font-bold ml-1 hidden md:inline"
								style={{ fontSize: "11px" }}
							>
								Trocar
							</span>
						)}
					</div>
				</div>

				{/* Ações e Navegação Superior */}
				<div className="layout-topbar-menu flex align-items-center gap-2">
					{navbarTopLeft && (
						<div className={classNames("flex align-items-center gap-1", { 'layout-topbar-menu-mobile-active': mobileTopbarMenuActive })}>
							{navbarTopLeft.map((item, index) => (
								<Link key={`top-left-menu-${index}`} to={item.to} className="no-underline">
									<Button label={item.label} icon={item.icon} className="p-button-text text-white p-button-sm" />
								</Link>
							))}
						</div>
					)}

					{navbarTopRight && (
						<div className={classNames("flex align-items-center gap-1", { 'layout-topbar-menu-mobile-active': mobileTopbarMenuActive })}>
							{navbarTopRight.map((item, index) => (
								<Link key={`top-right-menu-${index}`} to={item.to} className="no-underline">
									<Button label={item.label} icon={item.icon} className="p-button-text text-white p-button-sm" />
								</Link>
							))}
						</div>
					)}

					{/* Menu e Perfil do Usuário com Destaque Visual e Ação Clara */}
					<div className="layout-topbar-user flex align-items-center ml-2">
						<Menu model={appBarActionMenu} popup ref={userMenuRef} id="popup_user_menu" />
						<button
							type="button"
							className="user-topbar-pill p-link flex align-items-center gap-2 px-3 py-1 border-round-3xl transition-all transition-duration-150 cursor-pointer shadow-2"
							onClick={(e) => userMenuRef.current?.toggle(e)}
							aria-controls="popup_user_menu"
							aria-haspopup
							title="Abrir menu de perfil e saída"
						>
							<Avatar
								shape="circle"
								className="user-avatar-circle font-bold"
								label={auth.userName ? auth.userName.charAt(0).toUpperCase() : undefined}
								icon={!auth.userName ? "pi pi-user" : undefined}
								image={auth.userPhoto || undefined}
							/>
							<span className="font-bold text-sm user-name-label hidden md:inline">
								{auth.userName || "Usuário"}
							</span>
							<i className="pi pi-chevron-down text-xs user-chevron-icon" />
						</button>
					</div>
				</div>
			</div>

			{/* Sidebar Refinada */}
			<div className="layout-sidebar shadow-3" onClick={onSidebarClick}>
				<div className="sidebar-header text-center p-3 mb-2 border-bottom-1 surface-border">
					<Link to="/account" className="inline-block no-underline">
						{auth.userPhoto ? (
							<Image
								className="border-circle shadow-2"
								preview={false}
								style={{ width: '64px', height: '64px', objectFit: 'cover' }}
								imageSize="medium"
								src={auth.userPhoto}
							/>
						) : (
							<Avatar
								icon="pi pi-user"
								size="xlarge"
								shape="circle"
								className="bg-primary text-white shadow-2 font-bold"
								label={auth.userName ? auth.userName.charAt(0).toUpperCase() : undefined}
							/>
						)}
					</Link>
					<div className="text-900 font-bold text-center mt-2">
						Oi, {auth.userName || 'Usuário'}
					</div>
					{auth.userRole && (
						<div className="mt-1">
							<span className="text-xs font-semibold px-2 py-1 border-round surface-200 text-600 text-capitalize">
								{auth.userRole}
							</span>
						</div>
					)}
				</div>

				<AppMenu model={navbarSideLeft} onMenuItemClick={onMenuItemClick} />
			</div>

			{/* Conteúdo Principal */}
			<div className="layout-main-container">
				<div className="layout-main">
					<Outlet />
				</div>
			</div>
		</div>
	);
};

MainLayout.defaultProps = {
	primaryKey: 'id',
	apiPath: 'components_data/empid_option_list',
	fieldName: "empid",
	fieldValue: null,
	sortField: '',
	sortDir: '',
};

export default MainLayout;
